import { useState, useMemo } from 'react'
import type { CSSProperties } from 'react'
import type { GccDashboardData, GccOutsourcingScenario } from '@shared/types'
import { buildGccSyntheticFile, computeRetainedRolesForScenario } from './gccBridge'
import { GccErrorDetails } from './GccErrorDetails'
import { Badge } from './GccSourcingModel'
import { GCC_BADGE, GCC_SCENARIO_BADGE } from './gccExecutiveStyle'

interface Props {
  projectId: string
  gcc: GccDashboardData
  clientName?: string
  industry?: string
  usedScenarios?: GccOutsourcingScenario[]
  /** Scenarios whose analysis failed, with the reason. They stay in usedScenarios until deleted (WI 2399). */
  failedScenarios?: Partial<Record<GccOutsourcingScenario, string>>
  /** Deletes the failed analysis for a scenario so it can be run again. */
  onDeleteScenario?: (scenario: GccOutsourcingScenario) => Promise<void>
  onClose: () => void
  runTn?: (payload: { gccProjectId: string; scenario: GccOutsourcingScenario }) => Promise<{ jobId: string; projectId: string; error?: string }>
  onTnStarted?: (result: { jobId: string; projectId: string }, scenario: GccOutsourcingScenario) => void
}

const SCENARIO_DETAILS: Record<GccOutsourcingScenario, { title: string; desc: string; note?: string }> = {
  High:     { title: 'Aggressive Outsourcing',   desc: 'All GCC-eligible work moves offshore — only HQ/Markets roles stay onshore.' },
  Medium:   { title: 'Moderate Outsourcing',     desc: '80% of eligible work moves offshore — some eligible roles are partially retained.' },
  Low:      { title: 'Conservative Outsourcing', desc: '60% of eligible work moves offshore — a larger onshore footprint is maintained.' },
  Retained: {
    title: 'HQ + Markets — retained in every scenario',
    desc: 'HQ and Markets roles only, with no GCC-eligible residual. Combine it later with Medium or Low for a full onshore view.',
    note: 'Same workforce as High — runs as a standalone baseline with no offshore assumption.',
  },
}

const ALL_SCENARIOS: GccOutsourcingScenario[] = ['High', 'Medium', 'Low', 'Retained']

/** Shown on a scenario with no retained workforce; running it would produce nothing (WI 2422). */
const NO_WORKFORCE = 'No retained workforce in this design (0 HC), so there is nothing to analyse.'

export default function GccTnAnalysisModal({ projectId, gcc, clientName, industry, usedScenarios = [], failedScenarios = {}, onDeleteScenario, onClose, runTn, onTnStarted }: Props) {
  const allScenarios = ALL_SCENARIOS
  const scenarioHcMap = useMemo(
    () => Object.fromEntries(
      ALL_SCENARIOS.map(s => [
        s,
        computeRetainedRolesForScenario(gcc, s).reduce((sum, r) => sum + r.headcountEndState, 0),
      ])
    ) as Record<GccOutsourcingScenario, number>,
    [gcc]
  )
  const scenarioHc = (s: GccOutsourcingScenario) => scenarioHcMap[s]
  const canRun = (s: GccOutsourcingScenario) => !usedScenarios.includes(s) && scenarioHc(s) > 0
  const firstFree = allScenarios.find(canRun) ?? 'High'
  const [scenario, setScenario] = useState<GccOutsourcingScenario>(firstFree)
  const [step, setStep] = useState<'scenario' | 'review'>('scenario')
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<GccOutsourcingScenario | null>(null)
  // A failed run can be deleted and started again only if its scenario has a workforce.
  const canRetry = allScenarios.some(s => failedScenarios[s] !== undefined && usedScenarios.includes(s) && scenarioHc(s) > 0)

  const { syntheticFile, retainedCount, retainedHc } = useMemo(() => {
    const roles = computeRetainedRolesForScenario(gcc, scenario)
    return {
      syntheticFile: buildGccSyntheticFile(gcc, scenario),
      retainedCount: roles.length,
      retainedHc: roles.reduce((sum, r) => sum + r.headcountEndState, 0),
    }
  }, [gcc, scenario])

  const scenarioTitle = SCENARIO_DETAILS[scenario].title

  // Locked while the start request is in flight: leaving mid-start made the run look cancelled (WI 2367).
  const dismiss = () => { if (!running) onClose() }

  const handleDelete = async (s: GccOutsourcingScenario) => {
    if (!onDeleteScenario || deleting) return
    setDeleting(s)
    try {
      await onDeleteScenario(s)
    } catch {
      // The caller reports delete failures; the scenario simply stays Failed.
    } finally {
      setDeleting(null)
    }
  }

  const handleStart = async () => {
    if (!runTn || running) return
    setRunning(true)
    setError(null)
    try {
      const result = await runTn({ gccProjectId: projectId, scenario })
      if (result.error) throw new Error(result.error)
      onTnStarted?.({ jobId: result.jobId, projectId: result.projectId }, scenario)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start TN analysis.')
      setRunning(false)
    }
  }

  return (
    <div
      className="gx-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) dismiss() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="gcc-tn-analysis-title"
        className="gx-modal"
      >
        <div className="gx-modal-head">
          <div>
            <p className="gx-modal-eyebrow">GCC · TN Analysis</p>
            <h2 id="gcc-tn-analysis-title" className="gx-modal-title">Run TN Analysis on Retained Workforce</h2>
            <ol className="gx-steps">
              <li className={`gx-step${step === 'scenario' ? ' is-active' : ''}`} aria-current={step === 'scenario' ? 'step' : undefined}>
                1 · Scenario
              </li>
              <li aria-hidden="true">›</li>
              <li className={`gx-step${step === 'review' ? ' is-active' : ''}`} aria-current={step === 'review' ? 'step' : undefined}>
                2 · Review &amp; Start
              </li>
            </ol>
          </div>
          <button onClick={dismiss} disabled={running} aria-label="Close" className="gx-modal-close">✕</button>
        </div>

        {step === 'scenario' && (
          <>
            {/* Scenario picker */}
            <div className="gx-modal-body">
              <p className="gx-label">Workforce Scenario</p>
              <div className="gx-opts">
                {ALL_SCENARIOS.map(s => {
                  const { title: cardTitle, desc, note } = SCENARIO_DETAILS[s]
                  const selected = scenario === s
                  const alreadyExists = usedScenarios.includes(s)
                  const failure = alreadyExists ? failedScenarios[s] : undefined
                  const failed = failure !== undefined
                  // A used scenario keeps its Already exists / Failed + Delete display, even at 0 HC.
                  const noWorkforce = !alreadyExists && scenarioHc(s) === 0
                  const unavailable = alreadyExists || noWorkforce
                  const badge = unavailable ? GCC_BADGE.gray : GCC_SCENARIO_BADGE[s]
                  return (
                    <div key={s} className="relative">
                      <button
                        onClick={() => !unavailable && setScenario(s)}
                        disabled={unavailable}
                        aria-pressed={selected && !unavailable}
                        className={`gx-opt${failed ? ' is-failed' : unavailable ? ' is-disabled' : selected ? ' is-selected' : ''}`}
                        style={{ '--opt': badge.text } as CSSProperties}
                      >
                        <div className="gx-opt-row">
                          <Badge label={s} style={badge} />
                          <span className="gx-opt-title">{cardTitle}</span>
                          <span className="gx-opt-meta">{scenarioHc(s).toLocaleString()} HC</span>
                          {failed ? (
                            <span className="gx-status is-bad">· Failed</span>
                          ) : alreadyExists ? (
                            <span className="gx-status">· Already exists</span>
                          ) : noWorkforce && (
                            <span className="gx-status">· No workforce</span>
                          )}
                        </div>
                        <p className="gx-opt-desc">{desc}</p>
                        {note && <p className="gx-opt-desc">{note}</p>}
                        {noWorkforce && <p className="gx-opt-desc">{NO_WORKFORCE}</p>}
                        {failed && (
                          <p className={`gx-opt-desc is-bad truncate ${onDeleteScenario ? 'pr-16' : ''}`} title={failure}>{failure}</p>
                        )}
                      </button>
                      {failed && onDeleteScenario && (
                        <button
                          onClick={() => void handleDelete(s)}
                          disabled={deleting !== null}
                          aria-label={`Delete failed ${s} analysis`}
                          className="gx-btn-danger absolute bottom-3 right-4"
                        >
                          {deleting === s ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="gx-modal-foot">
              {!allScenarios.some(canRun) && (
                <p className="gx-cap mr-auto">
                  {canRetry
                    ? 'Delete a failed analysis to run that scenario again.'
                    : allScenarios.every(s => usedScenarios.includes(s))
                      ? 'All four scenarios already exist for this project.'
                      : 'No remaining scenario has a retained workforce to analyse.'}
                </p>
              )}
              <button onClick={dismiss} className="gx-btn">Cancel</button>
              {allScenarios.some(canRun) && (
                <button onClick={() => setStep('review')} disabled={!canRun(scenario)} className="gx-btn-primary">
                  Continue →
                </button>
              )}
            </div>
          </>
        )}

        {step === 'review' && (
          <>
            <div className="gx-modal-body">
              <p className="gx-cap">
                Review the details below. The TN pipeline will be pre-seeded with the retained workforce from your GCC analysis.
              </p>

              {/* Pre-filled fields summary */}
              <div className="gx-kv">
                {clientName && (
                  <div className="gx-kv-row">
                    <span className="gx-kv-k">Client</span>
                    <span className="gx-kv-v">{clientName}</span>
                  </div>
                )}
                {industry && (
                  <div className="gx-kv-row">
                    <span className="gx-kv-k">Industry</span>
                    <span className="gx-kv-v">{industry}</span>
                  </div>
                )}
                <div className="gx-kv-row">
                  <span className="gx-kv-k">Scenario</span>
                  <span className="gx-kv-v">
                    <Badge label={scenario} style={GCC_SCENARIO_BADGE[scenario]} />
                    <span>{scenarioTitle}</span>
                  </span>
                </div>
                <div className="gx-kv-row">
                  <span className="gx-kv-k">Workforce</span>
                  <span className="gx-kv-v">
                    <span>{retainedCount} retained roles</span>
                    <span aria-hidden="true">·</span>
                    <span className="gx-kv-n">{retainedHc.toLocaleString()} HC</span>
                    <button
                      onClick={() => {
                        const bytes = Uint8Array.from(atob(syntheticFile.base64), c => c.charCodeAt(0))
                        const blob = new Blob([bytes], { type: 'application/json' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = syntheticFile.name
                        a.click()
                        URL.revokeObjectURL(url)
                      }}
                      className="gx-link"
                      title="Download retained workforce JSON"
                    >
                      ({syntheticFile.name})
                    </button>
                  </span>
                </div>
              </div>

              <div className="gx-note">
                The pipeline will run Stages 1–6 using the retained workforce as the seed document. This typically takes 3–8 minutes.
              </div>

              {error && (
                <div className="text-center">
                  <GccErrorDetails message={error} />
                </div>
              )}
            </div>

            <div className="gx-modal-foot">
              <button
                onClick={() => { setStep('scenario'); setError(null) }}
                disabled={running}
                className="gx-btn-quiet"
              >
                ← Back
              </button>
              <button onClick={dismiss} disabled={running} className="gx-btn">Cancel</button>
              <button onClick={() => void handleStart()} disabled={running || !runTn} className="gx-btn-primary">
                {running ? 'Starting…' : 'Start Analysis →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
